import React, { Fragment, useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '../../../lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData, fetchTribes } from '../../../actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '../../../utils/tools/dataSyncingTools';
import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '../../../utils/tools/preventerTools';

// Constants

import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { AI_AN_CODE } from '../../../constants/ssap/hhmTypes/racesList';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '../../../constants/inputs/validationErrorMessages';



/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'American Indians and Alaskan Native' Page
 *
 */
const Page2_12 = props => {

    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams: {
                    hasSomeoneAIANSelfIdentified,
                    hasSomeoneFromFederallyRecognizedTribe
                }
            }
        },
        form,
        formName,
        householdMembers,
        tribes
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    useEffect(() => {
        // Fetching tribes:
        tribes.length === 0 && props.actions.fetchTribes();

        // To update existing apps to new AI/AN data model
        const newState = { ...data };
        newState.taxHousehold.householdMember.forEach(m => {

            const {
                americanIndianAlaskaNative: {
                    americanIndianAlaskaNativeSelfIdentification,
                    memberOfFederallyRecognizedTribeIndicator
                }
            } = m;
            
            if (americanIndianAlaskaNativeSelfIdentification && memberOfFederallyRecognizedTribeIndicator === null) {
                m.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = false;
            }
        });

        props.actions.changeData(newState);
    }, []);

    // Updating some props in JSON with the simultaneous check if
    // <memberOfFederallyRecognizedTribeIndicator> is false (to clear <americanIndianAlaskaNative> object)

    const onFieldChange = (newData, indexInArray) => {

        const currentState = newData.americanIndianAlaskaNative.state;

        if (currentState !== householdMembers[indexInArray].americanIndianAlaskaNative.state) {
            newData.americanIndianAlaskaNative.tribeName = null;
        }

        const newState = { ...data };
        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const AIAN_OBJECT = { label: 'American Indian or Alaska Native', code: AI_AN_CODE, otherLabel: null };
    const onFieldChange_member = (...args) => {

        const [ newData, indexInArray, , [ name, , , ] ] = args;

        const propName = name === 'americanIndianAlaskaNativeSelfIdentification'
            ? 'selfIdentification'
            : 'tribeRecognition';

        
        const newState = { ...data };
        const member = newState.taxHousehold.householdMember[indexInArray];

        /**
         * If a user sets AI/AN selfIdentification to false, all other
         * tribe data is cleared out and set to null.
         */
        if (!newData.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification) {
            member.americanIndianAlaskaNative.tribeName = null;
            member.americanIndianAlaskaNative.tribeFullName = null;
            member.americanIndianAlaskaNative.state = null;
            member.americanIndianAlaskaNative.stateFullName = null;
            member.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = null;
        }

        /**
         * If a user sets AI/AN selfIdentification to true, and tribeIndicator
         * hasn't yet been set (i.e. it is null), tribeIndicator is set to false.
         */
        if (newData.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification && newData.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator === null) {
            member.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = false;
        }

        /**
         * If a user sets tribeIndicator to false, all other
         * tribe data is cleared out and set to null.
         */
        if (!newData.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator) {
            member.americanIndianAlaskaNative.tribeName = null;
            member.americanIndianAlaskaNative.tribeFullName = null;
            member.americanIndianAlaskaNative.state = null;
            member.americanIndianAlaskaNative.stateFullName = null;

            /**
             * If this change was made with AI/AN selfIdentification
             * still true, we set tribeIndicator to false. If AI/AN
             * selfIdentification is additionally no longer true, we
             * reset tribeIndicator back to null.
             */
            if (newData.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification === true) {
                member.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = false;
            } else {
                member.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = null;
            }
        }

        let raceObj = newState
            .taxHousehold
            .householdMember[indexInArray]
            .ethnicityAndRace
            .race;

        const AIAN_exists = raceObj.some(race => race.code === AIAN_OBJECT.code);

        if(!newData.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification) {

            newState
                .taxHousehold
                .householdMember[indexInArray]
                .incomes = newState
                    .taxHousehold
                    .householdMember[indexInArray]
                    .incomes
                    .map(i => ({
                        ...i,
                        tribalAmount: 0
                    }));

            if (AIAN_exists) {
                newState
                    .taxHousehold
                    .householdMember[indexInArray]
                    .ethnicityAndRace
                    .race = raceObj.filter(race => race.code !== AIAN_OBJECT.code);
            }
        } else {
            newState.taxHousehold.householdMember[indexInArray] = newData;
            if (!AIAN_exists) {
                newState
                    .taxHousehold
                    .householdMember[indexInArray]
                    .ethnicityAndRace
                    .race
                    .push(AIAN_OBJECT);
            }
        }

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newData, , , [ name ] ] = args;

        const propName = name === 'hasSomeoneAIANSelfIdentified'
            ? 'selfIdentification'
            : 'tribeRecognition';

        const updateIndividualValues = hhm => {
            hhm.americanIndianAlaskaNative.tribeName = null;
            hhm.americanIndianAlaskaNative.tribeFullName = null;
            hhm.americanIndianAlaskaNative.state = null;
            hhm.americanIndianAlaskaNative.stateFullName = null;
            hhm.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = null;

            switch (name) {

                case 'hasSomeoneAIANSelfIdentified':
                    hhm.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification = false;

                    hhm.incomes = hhm.incomes.map(i => ({
                        ...i,
                        tribalAmount: 0
                    }));

                    hhm
                        .ethnicityAndRace
                        .race = hhm
                            .ethnicityAndRace
                            .race
                            .filter(race => race.code !== AIAN_OBJECT.code);
                    break;

                case 'hasSomeoneFromFederallyRecognizedTribe':
                    if (hhm.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification) {
                        hhm.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator = false;
                    }
                    break;
            }

            return hhm;
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName
            }
        );

        props.actions.changeData(syncedData);
    };

    const getAIANCommunityIdentification = (householdMembers) => {

        const combinedMembers = [];

        householdMembers.forEach((member, i) => {
            const {
                americanIndianAlaskaNative: {
                    americanIndianAlaskaNativeSelfIdentification
                },
                applicantGuid,
                name
            } = member;

            combinedMembers.push(
                <Fragment key = { `${ applicantGuid }_self_identification` } >
                    <ul className = 'usa-list usa-list--unstyled' >
                        <li>
                            <UniversalInput
                                label = { {
                                    text: 'common.input.label.non_internationalized_text',
                                    labelTextReplacements: {
                                        text: normalizeNameOfPerson(name)
                                    }
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'americanIndianAlaskaNativeSelfIdentification',
                                    classes: [ '' ],
                                    checked: americanIndianAlaskaNativeSelfIdentification,
                                    value: americanIndianAlaskaNativeSelfIdentification,
                                    currentData: member,
                                    fields: [ 'americanIndianAlaskaNative' ]
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_member
                                } }
                                indexInArray = { i }
                            />
                        </li>
                    </ul>
                </Fragment>
            );
        });

        combinedMembers.push(
            <UniversalInput
                key = 'hasSomeoneAIANSelfIdentified'
                label = { {
                    text: 'ssap.input.label.none_of_above',
                    isLabelRequired: false
                } }
                inputData = { {
                    isInputRequired: false,
                    type: INPUT_TYPES.CHECKBOX,
                    name: 'hasSomeoneAIANSelfIdentified',
                    checked: hasSomeoneAIANSelfIdentified === false,
                    value: hasSomeoneAIANSelfIdentified === false,
                    currentData: cloneObject(data),
                    fields: [ 'taxHousehold', 'thCommonParams' ],
                    reversedValue: true
                } }
                inputActions = { {
                    onFieldChange: onFieldChange_none_of_above
                } }
            />
        );

        return combinedMembers;
    };

    const getFederallyRecognizedTribeInputs = (householdMembers, tribes) => {

        const combinedMembers = [];

        householdMembers.forEach((member, i) => {
            const {
                americanIndianAlaskaNative: {
                    americanIndianAlaskaNativeSelfIdentification,
                    tribeName,
                    state,
                    memberOfFederallyRecognizedTribeIndicator
                },
                applicantGuid,
                name
            } = member;
            const memberName = `${normalizeNameOfPerson(name)}'s`;

            americanIndianAlaskaNativeSelfIdentification === true &&

            combinedMembers.push(
                <Fragment key = { `${ applicantGuid }_tribe_recognition` }>
                    <ul className = 'usa-list usa-list--unstyled' >
                        <li>
                            <UniversalInput
                                label = { {
                                    text: 'common.input.label.non_internationalized_text',
                                    labelTextReplacements: {
                                        text: normalizeNameOfPerson(name)
                                    }
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'memberOfFederallyRecognizedTribeIndicator',
                                    classes: [ '' ],
                                    checked: memberOfFederallyRecognizedTribeIndicator,
                                    value: memberOfFederallyRecognizedTribeIndicator,
                                    currentData: member,
                                    fields: [ 'americanIndianAlaskaNative' ],
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_member
                                } }
                                indexInArray = { i }
                            />
                        </li>
                    </ul>

                    {
                        memberOfFederallyRecognizedTribeIndicator &&

                        <Fragment>
                            <div className = 'grid-col-12 margin-b-20 gi-dropdown-layout--02' >
                                <fieldset className = 'usa-fieldset' >
                                    <legend>
                                        <FormattedMessage
                                            id = 'ssap.family_household.ai_an.select_membership'
                                            values = { {
                                                memberName: <strong>{ memberName }</strong>
                                            } }
                                        />
                                    </legend>
                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.state',
                                            isLabelRequired: true
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.DROPDOWN,
                                            name: 'state',
                                            value: state,
                                            options: DROPDOWN_OPTIONS.STATES_FOR_TRIBES(tribes),
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: cloneObject(member),
                                            fields: [ 'americanIndianAlaskaNative' ]
                                        } }
                                        errorClasses = { [ 'margin-b-0' ] }
                                        inputActions = { {
                                            onFieldChange
                                        } }
                                        indexInArray = { i }
                                    />
                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.tribal_name',
                                            isLabelRequired: true
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.DROPDOWN,
                                            name: 'tribeName',
                                            value: tribeName,
                                            options: DROPDOWN_OPTIONS.TRIBES(tribes, state),
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: member,
                                            fields: [ 'americanIndianAlaskaNative' ]
                                        } }
                                        errorClasses = { [ 'margin-b-0' ] }
                                        inputActions = { {
                                            onFieldChange
                                        } }
                                        indexInArray = { i }
                                    />
                                </fieldset>
                            </div>
                        </Fragment>
                    }
                </Fragment>
            );
        });

        combinedMembers.push(
            <UniversalInput
                key = 'hasSomeoneFromFederallyRecognizedTribe'
                label = { {
                    text: 'ssap.input.label.none_of_above',
                    isLabelRequired: false
                } }
                inputData = { {
                    isInputRequired: false,
                    type: INPUT_TYPES.CHECKBOX,
                    name: 'hasSomeoneFromFederallyRecognizedTribe',
                    checked: hasSomeoneFromFederallyRecognizedTribe === false,
                    value: hasSomeoneFromFederallyRecognizedTribe === false,
                    currentData: cloneObject(data),
                    fields: [ 'taxHousehold', 'thCommonParams' ],
                    reversedValue: true
                } }
                inputActions = { {
                    onFieldChange: onFieldChange_none_of_above
                } }
            />
        );

        return combinedMembers;
    };

    const topLevelValidity = useMemo(
        () => {
            if (hasSomeoneAIANSelfIdentified === false) {
                return true;
            } else {
                return householdMembers.some(hhm => hhm.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification)
                    && (
                        hasSomeoneFromFederallyRecognizedTribe === false
                        || householdMembers
                            .some(hhm => hhm.americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator)
                    );
            }
        },
        [ householdMembers, hasSomeoneAIANSelfIdentified, hasSomeoneFromFederallyRecognizedTribe ]
    );

    return (
        <Fragment>
            <div
                className = 'fade-in'
                id = 'fhAdditionalInfo'
            >
                <div className = 'subsection' >
                    <Form
                        { ...{
                            formName,
                            topLevelValidity
                        } }
                    >
                        <InstructionalText
                            Component = {
                                <p data-instructions = 'instructional_text.ai_an_information' >
                                    { 'ssap.family_household.ai_an.are_ai_an' }
                                </p>
                            }
                            required
                        />
                        {
                            getAIANCommunityIdentification(householdMembers)
                        }
                        {
                            householdMembers.some(m => m.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification) &&

                            <Fragment>
                                <p className = 'required-true padding-t-20' >
                                    <FormattedMessage id = 'ssap.family_household.ai_an.belong_to_tribe' />
                                </p>
                                { getFederallyRecognizedTribeInputs(householdMembers, tribes) }
                            </Fragment>
                        }
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !topLevelValidity }
                            messageText = { VALIDATION_ERROR_MESSAGES.ALL_QUESTIONS }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    tribes: state.ssap.tribes
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        fetchTribes
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_12);
